import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus, Search, Download, Upload, RefreshCw, Trash2, X as XIcon,
  MoreHorizontal, ArrowUpDown, PencilLine,
} from "lucide-react";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatAED, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import AdditionalComplianceForm, {
  ADDITIONAL_TYPES, ADDITIONAL_STATUSES, ADDITIONAL_INVOICE_STATUSES,
} from "@/components/additional/AdditionalComplianceForm";
import AdditionalImportDialog from "@/components/additional/AdditionalImportDialog";
import logger from "@/lib/logger";

const INVOICE_TONE = {
  "Invoiced": "bg-blue-50 text-blue-700 border-blue-200",
  "Pending": "bg-amber-50 text-amber-700 border-amber-200",
  "Paid": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Partly Paid": "bg-orange-50 text-orange-700 border-orange-200",
};
const STATUS_TONE = {
  "Pending": "bg-amber-50 text-amber-700 border-amber-200",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
  "Under Review": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Filed": "bg-teal-50 text-teal-700 border-teal-200",
};

function InvoiceStatusInline({ record, onChanged }) {
  const [saving, setSaving] = useState(false);
  const current = record.invoice_status || "Pending";
  const onSelect = async (v) => {
    if (v === current) return;
    setSaving(true);
    try {
      await api.patch(`/additional-compliances/${record.id}`, { invoice_status: v });
      toast.success(`Invoice status → ${v}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };
  return (
    <Select value={current} onValueChange={onSelect} disabled={saving}>
      <SelectTrigger
        className={`h-7 px-2 text-xs w-[130px] border ${INVOICE_TONE[current] || INVOICE_TONE["Pending"]}`}
        data-testid={`add-invoice-status-${record.id}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ADDITIONAL_INVOICE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function StatusInline({ record, onChanged }) {
  const [saving, setSaving] = useState(false);
  const current = record.status || "Pending";
  const onSelect = async (v) => {
    if (v === current) return;
    setSaving(true);
    try {
      await api.patch(`/additional-compliances/${record.id}`, { status: v });
      toast.success(`Status → ${v}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };
  return (
    <Select value={current} onValueChange={onSelect} disabled={saving}>
      <SelectTrigger
        className={`h-7 px-2 text-xs w-[140px] border ${STATUS_TONE[current] || STATUS_TONE["Pending"]}`}
        data-testid={`add-status-${record.id}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ADDITIONAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function InlineTextField({ record, field, label, testidPrefix, onChanged, width = 120, placeholder = "—" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(record[field] || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(record[field] || ""); }, [record, field]);
  const save = async () => {
    if (val === (record[field] || "")) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/additional-compliances/${record.id}`, { [field]: val });
      toast.success(`${label} updated`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
      setVal(record[field] || "");
    } finally { setSaving(false); setEditing(false); }
  };
  if (!editing) {
    return (
      <button
        type="button"
        className="text-sm text-slate-800 hover:bg-slate-100 px-2 py-0.5 rounded-md text-left block truncate"
        style={{ minWidth: width, maxWidth: width * 1.6 }}
        onClick={() => setEditing(true)}
        data-testid={`${testidPrefix}-${record.id}`}
        title={record[field] || `Click to edit ${label.toLowerCase()}`}
      >
        {record[field] ? record[field] : <span className="text-slate-400 italic">{placeholder}</span>}
      </button>
    );
  }
  return (
    <Input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") save();
        else if (e.key === "Escape") { setVal(record[field] || ""); setEditing(false); }
      }}
      disabled={saving}
      className="h-7 text-xs"
      style={{ width }}
      data-testid={`${testidPrefix}-input-${record.id}`}
    />
  );
}

function JurisdictionInline({ record, onChanged }) {
  return <InlineTextField record={record} field="jurisdiction" label="Jurisdiction" testidPrefix="add-jurisdiction" onChanged={onChanged} width={120} />;
}
function RemarksInline({ record, onChanged }) {
  return <InlineTextField record={record} field="remarks" label="Remarks" testidPrefix="add-remarks" onChanged={onChanged} width={180} placeholder="Add remarks" />;
}
function PartnerInline({ record, onChanged }) {
  return <InlineTextField record={record} field="partner_name" label="Partner" testidPrefix="add-partner" onChanged={onChanged} width={120} />;
}
function Person1Inline({ record, onChanged }) {
  return <InlineTextField record={record} field="person_1" label="Person 1" testidPrefix="add-person1" onChanged={onChanged} width={110} />;
}
function ReviewerInline({ record, onChanged }) {
  return <InlineTextField record={record} field="reviewer" label="Reviewer" testidPrefix="add-reviewer" onChanged={onChanged} width={110} />;
}

function DeadlineInline({ record, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(record.deadline || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(record.deadline || ""); }, [record]);
  const save = async () => {
    if (val === (record.deadline || "")) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/additional-compliances/${record.id}`, { deadline: val });
      toast.success("Deadline updated");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
      setVal(record.deadline || "");
    } finally { setSaving(false); setEditing(false); }
  };
  if (!editing) {
    return (
      <button
        type="button"
        className="text-sm text-slate-800 hover:bg-slate-100 px-2 py-0.5 rounded-md text-left block truncate font-mono-num"
        style={{ minWidth: 110 }}
        onClick={() => setEditing(true)}
        data-testid={`add-deadline-${record.id}`}
        title="Click to edit deadline"
      >
        {record.deadline ? formatDate(record.deadline) : <span className="text-slate-400 italic">YYYY-MM-DD</span>}
      </button>
    );
  }
  return (
    <Input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") save();
        else if (e.key === "Escape") { setVal(record.deadline || ""); setEditing(false); }
      }}
      disabled={saving}
      placeholder="YYYY-MM-DD"
      className="h-7 text-xs font-mono-num"
      style={{ width: 130 }}
      data-testid={`add-deadline-input-${record.id}`}
    />
  );
}

function SortBtn({ label, k, sortKey, sortOrder, onClick }) {
  const active = sortKey === k;
  return (
    <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => onClick(k)}>
      {label}
      <ArrowUpDown className={cn("w-3 h-3", active ? "text-blue-600" : "text-slate-400")} />
      {active && <span className="text-[10px] text-slate-500 font-mono-num">{sortOrder}</span>}
    </button>
  );
}

function BulkEditDialog({ open, onClose, ids, onDone }) {
  const [fields, setFields] = useState({
    invoice_status: { enabled: false, value: "Invoiced" },
    status: { enabled: false, value: "In Progress" },
    jurisdiction: { enabled: false, value: "" },
    partner_name: { enabled: false, value: "" },
    person_1: { enabled: false, value: "" },
    reviewer: { enabled: false, value: "" },
    period: { enabled: false, value: "" },
    remarks: { enabled: false, value: "" },
  });
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setFields((f) => Object.fromEntries(Object.entries(f).map(([k, v]) => [k, { ...v, enabled: false }])));
  };

  const submit = async () => {
    const updates = {};
    Object.entries(fields).forEach(([k, v]) => { if (v.enabled) updates[k] = v.value; });
    if (Object.keys(updates).length === 0) {
      toast.error("Enable at least one field to update");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/additional-compliances/bulk-edit", { ids, updates });
      toast.success(`Updated ${data.updated} record(s)`);
      reset();
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk edit failed");
    } finally { setSaving(false); }
  };

  const rowFor = (key, label, control) => (
    <div className="grid grid-cols-[24px_140px_1fr] gap-2 items-center">
      <Checkbox
        checked={fields[key].enabled}
        onCheckedChange={(v) => setFields((f) => ({ ...f, [key]: { ...f[key], enabled: !!v } }))}
        data-testid={`bulk-edit-toggle-${key}`}
      />
      <Label className="text-xs uppercase tracking-widest text-slate-600">{label}</Label>
      <div className={fields[key].enabled ? "" : "opacity-50 pointer-events-none"}>{control}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Bulk edit {ids.length} record(s)</DialogTitle>
          <DialogDescription>Only the enabled fields will be updated. Others are left unchanged.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3" data-testid="bulk-edit-body">
          {rowFor("invoice_status", "Invoice Status",
            <Select value={fields.invoice_status.value} onValueChange={(v) => setFields((f) => ({ ...f, invoice_status: { ...f.invoice_status, value: v } }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ADDITIONAL_INVOICE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {rowFor("status", "Status",
            <Select value={fields.status.value} onValueChange={(v) => setFields((f) => ({ ...f, status: { ...f.status, value: v } }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ADDITIONAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {rowFor("jurisdiction", "Jurisdiction",
            <Input value={fields.jurisdiction.value} onChange={(e) => setFields((f) => ({ ...f, jurisdiction: { ...f.jurisdiction, value: e.target.value } }))} />
          )}
          {rowFor("period", "Period",
            <Input value={fields.period.value} onChange={(e) => setFields((f) => ({ ...f, period: { ...f.period, value: e.target.value } }))} />
          )}
          {rowFor("partner_name", "Partner Name",
            <Input value={fields.partner_name.value} onChange={(e) => setFields((f) => ({ ...f, partner_name: { ...f.partner_name, value: e.target.value } }))} />
          )}
          {rowFor("person_1", "Person 1",
            <Input value={fields.person_1.value} onChange={(e) => setFields((f) => ({ ...f, person_1: { ...f.person_1, value: e.target.value } }))} />
          )}
          {rowFor("reviewer", "Reviewer",
            <Input value={fields.reviewer.value} onChange={(e) => setFields((f) => ({ ...f, reviewer: { ...f.reviewer, value: e.target.value } }))} />
          )}
          {rowFor("remarks", "Remarks",
            <Input value={fields.remarks.value} onChange={(e) => setFields((f) => ({ ...f, remarks: { ...f.remarks, value: e.target.value } }))} placeholder="Applied to all selected rows" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="bulk-edit-submit">
            {saving ? "Applying…" : "Apply bulk edit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdditionalCompliances({
  moduleTitle = "Audit and Other Compliance",
  moduleTypes = null,          // if provided, only these types are allowed for filtering + form
  fixedTypeFilter = null,      // if provided, pins the type filter (hides it) — e.g. "Corporate Tax page"
  showDeadlineInsteadOfReportNumber = false, // Corporate Tax: swap Report Number column with Deadline
} = {}) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [periods, setPeriods] = useState([]);
  const [person1Names, setPerson1Names] = useState([]);
  const [reviewerNames, setReviewerNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [period, setPeriod] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [status, setStatus] = useState("");
  const [person1, setPerson1] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [remarksFilter, setRemarksFilter] = useState("");

  const [sortKey, setSortKey] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [pageSize, setPageSize] = useState(100); // 25 / 50 / 100 / 0 (All)
  const [page, setPage] = useState(1);

  const canDelete = user?.role === "administrator";
  // Types shown in the type dropdown / form. If moduleTypes is set, restrict to those.
  const availableTypes = moduleTypes || ADDITIONAL_TYPES;

  const buildParams = useCallback((extra = {}) => {
    const p = {};
    if (q) p.q = q;
    if (type) p.compliance_type = type;
    if (moduleTypes && moduleTypes.length) p.types = moduleTypes.join(",");
    if (period) p.period = period;
    if (invoiceStatus) p.invoice_status = invoiceStatus;
    if (status) p.status = status;
    if (person1) p.person_1 = person1;
    if (reviewer) p.reviewer = reviewer;
    if (remarksFilter) p.remarks = remarksFilter;
    return { ...p, ...extra };
  }, [q, type, period, invoiceStatus, status, person1, reviewer, remarksFilter, moduleTypes]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const limit = pageSize; // 0 = all
      const skip = pageSize > 0 ? (page - 1) * pageSize : 0;
      const params = buildParams({ sort: sortKey, order: sortOrder, skip, limit });
      const { data } = await api.get("/additional-compliances", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPeriods(data.periods || []);
      setPerson1Names(data.person_1_names || []);
      setReviewerNames(data.reviewer_names || []);
    } catch (e) {
      logger.error("Failed to load additional compliances:", e);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, [buildParams, sortKey, sortOrder, page, pageSize]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const allSelected = items.length > 0 && items.every((c) => selected.has(c.id));
  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allSelected) items.forEach((c) => n.delete(c.id));
    else items.forEach((c) => n.add(c.id));
    return n;
  });
  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const clearSel = () => setSelected(new Set());

  const sortToggle = (key) => {
    if (sortKey === key) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortOrder("asc"); }
  };

  const resetFilters = () => {
    setQ(""); setType(""); setPeriod(""); setInvoiceStatus(""); setStatus("");
    setPerson1(""); setReviewer(""); setRemarksFilter("");
    setPage(1);
  };

  const exportCsv = () => {
    const params = new URLSearchParams(buildParams()).toString();
    const url = `${API_BASE}/additional-compliances/export.csv${params ? "?" + params : ""}`;
    window.open(url, "_blank");
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete Additional record for ${c.client_name}?`)) return;
    try {
      await api.delete(`/additional-compliances/${c.id}`);
      toast.success("Deleted");
      fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected record(s)?`)) return;
    try {
      const { data } = await api.post("/additional-compliances/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted}`);
      clearSel();
      fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk delete failed");
    }
  };

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  return (
    <AppLayout
      title={moduleTitle}
      actions={
        <>
          <Button variant="outline" onClick={fetchItems} data-testid="add-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="add-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
          <Button variant="outline" onClick={() => setShowImport(true)} data-testid="add-import-csv">
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV
          </Button>
          <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-additional-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Record
          </Button>
        </>
      }
    >
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search client name…" value={q}
                   onChange={(e) => { setQ(e.target.value); setPage(1); }}
                   className="pl-9" data-testid="add-filter-search" />
          </div>
          <Select value={type || "all"} onValueChange={(v) => { setType(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[180px]" data-testid="add-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {availableTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={period || "all"} onValueChange={(v) => { setPeriod(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[160px]" data-testid="add-filter-period"><SelectValue placeholder="Period" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All periods</SelectItem>
              {periods.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={invoiceStatus || "all"} onValueChange={(v) => { setInvoiceStatus(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[160px]" data-testid="add-filter-invoice"><SelectValue placeholder="Invoice Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All invoice statuses</SelectItem>
              {ADDITIONAL_INVOICE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[150px]" data-testid="add-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {ADDITIONAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={person1 || "all"} onValueChange={(v) => { setPerson1(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[160px]" data-testid="add-filter-person1"><SelectValue placeholder="Person 1" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Person 1</SelectItem>
              {person1Names.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={reviewer || "all"} onValueChange={(v) => { setReviewer(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[160px]" data-testid="add-filter-reviewer"><SelectValue placeholder="Reviewer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reviewers</SelectItem>
              {reviewerNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Filter remarks…"
              value={remarksFilter}
              onChange={(e) => { setRemarksFilter(e.target.value); setPage(1); }}
              className="pl-9"
              data-testid="add-filter-remarks"
            />
          </div>
          <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="add-filter-reset">Reset</Button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5" data-testid="add-bulk-bar">
          <div className="text-sm text-blue-900">
            <span className="font-semibold font-mono-num">{selected.size}</span> record(s) selected
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowBulkEdit(true)} data-testid="add-bulk-edit">
              <PencilLine className="w-4 h-4 mr-1.5" /> Bulk edit
            </Button>
            {canDelete && (
              <Button variant="destructive" size="sm" onClick={bulkDelete} data-testid="add-bulk-delete">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete selected
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={clearSel} data-testid="add-bulk-clear">
              <XIcon className="w-4 h-4 mr-1.5" /> Clear
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="px-3 w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="add-select-all" />
                </TableHead>
                <TableHead className="px-4">Type</TableHead>
                <TableHead>Client Name</TableHead>
                {showDeadlineInsteadOfReportNumber
                  ? <TableHead>
                      <SortBtn label="Deadline" k="deadline" sortKey={sortKey} sortOrder={sortOrder} onClick={sortToggle} />
                    </TableHead>
                  : <TableHead>
                      <SortBtn label="Report No." k="report_number" sortKey={sortKey} sortOrder={sortOrder} onClick={sortToggle} />
                    </TableHead>}
                <TableHead>Partner</TableHead>
                <TableHead>Person 1</TableHead>
                <TableHead>Reviewer</TableHead>
                <TableHead className="text-right">
                  <SortBtn label="Fees" k="fees" sortKey={sortKey} sortOrder={sortOrder} onClick={sortToggle} />
                </TableHead>
                <TableHead>Invoice Status</TableHead>
                <TableHead>Jurisdiction</TableHead>
                <TableHead>
                  <SortBtn label="Period" k="period" sortKey={sortKey} sortOrder={sortOrder} onClick={sortToggle} />
                </TableHead>
                {!showDeadlineInsteadOfReportNumber && (
                  <TableHead>
                    <SortBtn label="Deadline" k="deadline" sortKey={sortKey} sortOrder={sortOrder} onClick={sortToggle} />
                  </TableHead>
                )}
                <TableHead>Status</TableHead>
                <TableHead>Remarks</TableHead>
                <TableHead className="text-right pr-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={showDeadlineInsteadOfReportNumber ? 14 : 15} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={showDeadlineInsteadOfReportNumber ? 14 : 15} className="text-center py-16 text-slate-500">No additional compliance records yet.</TableCell></TableRow>
              ) : items.map((c) => (
                <TableRow key={c.id} className="data-row" data-testid={`add-row-${c.id}`}>
                  <TableCell className="px-3">
                    <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} data-testid={`add-check-${c.id}`} />
                  </TableCell>
                  <TableCell className="px-4 text-[11px] uppercase tracking-widest font-semibold text-slate-700">{c.compliance_type}</TableCell>
                  <TableCell className="font-medium text-slate-900">{c.client_name}</TableCell>
                  {showDeadlineInsteadOfReportNumber
                    ? <TableCell><DeadlineInline record={c} onChanged={fetchItems} /></TableCell>
                    : <TableCell className="text-sm text-slate-700 font-mono-num">{c.report_number || "—"}</TableCell>}
                  <TableCell><PartnerInline record={c} onChanged={fetchItems} /></TableCell>
                  <TableCell><Person1Inline record={c} onChanged={fetchItems} /></TableCell>
                  <TableCell><ReviewerInline record={c} onChanged={fetchItems} /></TableCell>
                  <TableCell className="text-right font-mono-num text-slate-800">{formatAED(c.fees)}</TableCell>
                  <TableCell><InvoiceStatusInline record={c} onChanged={fetchItems} /></TableCell>
                  <TableCell><JurisdictionInline record={c} onChanged={fetchItems} /></TableCell>
                  <TableCell className="text-sm text-slate-700 font-mono-num">{c.period || "—"}</TableCell>
                  {!showDeadlineInsteadOfReportNumber && <TableCell><DeadlineInline record={c} onChanged={fetchItems} /></TableCell>}
                  <TableCell><StatusInline record={c} onChanged={fetchItems} /></TableCell>
                  <TableCell><RemarksInline record={c} onChanged={fetchItems} /></TableCell>
                  <TableCell className="text-right pr-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`add-actions-${c.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => { setEditing(c); setShowForm(true); }} data-testid={`add-edit-${c.id}`}>Edit</DropdownMenuItem>
                        {canDelete && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600 focus:text-red-700" onClick={() => remove(c)} data-testid={`add-delete-${c.id}`}>
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 text-sm text-slate-600">
          <div>Showing <span className="font-medium">{items.length}</span> of <span className="font-medium font-mono-num">{total}</span> records</div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Rows per page:</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-8 w-[90px]" data-testid="add-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="0">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {pageSize > 0 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="add-page-prev">Prev</Button>
                <span className="text-xs px-2 font-mono-num">Page {page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="add-page-next">Next</Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <AdditionalComplianceForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        initial={editing}
        onSaved={fetchItems}
        availableTypes={availableTypes}
      />
      <AdditionalImportDialog open={showImport} onClose={() => setShowImport(false)} onDone={fetchItems} />
      <BulkEditDialog
        open={showBulkEdit}
        onClose={() => setShowBulkEdit(false)}
        ids={selectedIds}
        onDone={() => { clearSel(); fetchItems(); }}
      />
    </AppLayout>
  );
}
